import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  MagnifyingGlass, FunnelSimple, ArrowsClockwise, XCircle, Play, DownloadSimple, CheckCircle,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n || 0).toLocaleString('es-AR');

const STATUS_COLORS = {
  awaiting_payment: 'bg-orange-500/15 text-orange-500 border-orange-500/30',
  payment_review: 'bg-purple-500/15 text-purple-500 border-purple-500/30',
  pending: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/30',
  processing: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
  in_progress: 'bg-cyan-500/15 text-cyan-500 border-cyan-500/30',
  completed: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  cancelled: 'bg-red-500/15 text-red-500 border-red-500/30',
  failed: 'bg-red-500/15 text-red-500 border-red-500/30',
};

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'awaiting_payment', label: 'Esperando pago' },
  { value: 'payment_review', label: 'Comprobante en revisión' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'processing', label: 'En proceso' },
  { value: 'in_progress', label: 'Ejecutando' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'failed', label: 'Falla' },
];

const PLATFORMS = ['', 'Instagram', 'TikTok', 'Facebook'];

export const OrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [platform, setPlatform] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const buildParams = () => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    if (platform) p.set('platform', platform);
    if (userEmail) p.set('user_email', userEmail);
    if (dateFrom) p.set('date_from', new Date(dateFrom).toISOString());
    if (dateTo) p.set('date_to', new Date(dateTo).toISOString());
    p.set('limit', '200');
    return p.toString();
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/orders/filter?${buildParams()}`, {
        withCredentials: true,
      });
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Error al filtrar pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const retry = async (o) => {
    setBusy((b) => ({ ...b, [o.id]: 'retry' }));
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/orders/${o.id}/retry`, {}, { withCredentials: true });
      if (data.ok) toast.success('Pedido re-enviado');
      else toast.error(data.message || 'No se pudo re-enviar');
      fetch();
    } catch (e) {
      toast.error('Error al reintentar');
    } finally {
      setBusy((b) => ({ ...b, [o.id]: null }));
    }
  };

  const cancel = async (o) => {
    if (!window.confirm(`¿Cancelar el pedido #${o.id.slice(0, 8)}?`)) return;
    setBusy((b) => ({ ...b, [o.id]: 'cancel' }));
    try {
      await axios.post(`${API_URL}/api/admin/orders/${o.id}/cancel`, {}, { withCredentials: true });
      toast.success('Pedido cancelado');
      fetch();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al cancelar';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy((b) => ({ ...b, [o.id]: null }));
    }
  };

  const exportCsv = () => {
    const url = `${API_URL}/api/admin/orders/export.csv`;
    // Abrir en nueva pestaña — el navegador usará las cookies http-only automáticamente
    window.open(url, '_blank');
  };

  const clearFilters = () => {
    setQ(''); setStatus(''); setPlatform(''); setDateFrom(''); setDateTo(''); setUserEmail('');
    setTimeout(fetch, 50);
  };

  return (
    <div className="space-y-4" data-testid="orders-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Pedidos</h2>
          <p className="text-sm text-muted-foreground">{fmt(total)} pedidos en total con los filtros aplicados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetch} data-testid="orders-refresh">
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button size="sm" onClick={exportCsv} data-testid="orders-export">
            <DownloadSimple size={14} className="mr-1.5" /> Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border-border" data-testid="orders-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><MagnifyingGlass size={12} /> Buscar</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ID / email / URL / order externo" data-testid="filter-q" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full h-9 px-3 rounded-md bg-background border border-input text-sm"
              data-testid="filter-status"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Plataforma</Label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full h-9 px-3 rounded-md bg-background border border-input text-sm"
              data-testid="filter-platform"
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>{p || 'Todas'}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Email cliente</Label>
            <Input value={userEmail} onChange={(e) => setUserEmail(e.target.value)} placeholder="filtrar por email" data-testid="filter-email" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={fetch} data-testid="filter-apply"><FunnelSimple size={14} className="mr-1.5" /> Aplicar</Button>
          <Button size="sm" variant="outline" onClick={clearFilters} data-testid="filter-clear">Limpiar</Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-border overflow-hidden" data-testid="orders-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">ID</th>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Servicio</th>
                <th className="text-right px-3 py-2">Cantidad</th>
                <th className="text-right px-3 py-2">Precio</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-right px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-muted-foreground">Sin pedidos con estos filtros.</td>
                </tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`order-row-${o.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{o.created_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{o.service_name}</div>
                    <div className="text-xs text-muted-foreground">{o.platform}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(o.quantity)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">${fmt(o.price)}</td>
                  <td className="px-3 py-2">
                    <Badge className={STATUS_COLORS[o.status] || ''} variant="outline">{o.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{o.user_name || '—'}</div>
                    <div className="text-muted-foreground">{o.user_email || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {['failed', 'cancelled', 'processing'].includes(o.status) && (
                      <Button size="sm" variant="outline" onClick={() => retry(o)} disabled={busy[o.id] === 'retry'} data-testid={`order-retry-${o.id}`} className="mr-1">
                        <Play size={12} weight="fill" className="mr-1" /> Reintentar
                      </Button>
                    )}
                    {o.status !== 'completed' && o.status !== 'cancelled' && (
                      <Button size="sm" variant="outline" onClick={() => cancel(o)} disabled={busy[o.id] === 'cancel'} data-testid={`order-cancel-${o.id}`}>
                        <XCircle size={12} weight="fill" className="mr-1" /> Cancelar
                      </Button>
                    )}
                    {o.status === 'completed' && (
                      <span className="text-emerald-500 text-xs inline-flex items-center gap-1">
                        <CheckCircle size={12} weight="fill" /> OK
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
